/** Draft and build commands: card offers (open/close/reroll/skip), placing segments and towers, upgrades, selling, priorities. */
import { CardsData } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { Cell } from "../../world/cell";
import { Card, CardKind, OfferKind, Tower } from "../entities";
import { DueKinds, HandMix, HandPlan, HandSupply, MIN_CHOICE, PaceIntervals, planHand, rerollPlan } from "../handPlan";
import { SimEventKind } from "../events";
import { CommandResult, TargetPriority } from "../command";
import { LevelPhase, LevelSim } from "../levelSim";
import { recomputeAuras } from "./combat";
import { disbandSquad, promoteSquad, raiseSquad, relocateSquad, rerouteSquads } from "../barracks";
import { BASE_TIERS, ascend, dealSpecial, redealSpecial, resetBranchState, specialInHand } from "../branches/ascension";

export function pickCard(sim: LevelSim, index: number): CommandResult {
  if (!sim.offerOpen) return CommandResult.NoOffer;
  if (sim.offer.hasPick) return CommandResult.AlreadyPicked;
  if (index < 0 || index >= sim.offer.cards.length) return CommandResult.InvalidIndex;
  sim.offer.pickedIndex = index;
  const card = sim.offer.pickedCard;
  if (card.kind === CardKind.Path) {
    sim.offer.ghosts = sim.road.enumerateValidPlacements(sim.balance.cards.shapes[card.index]);
    if (sim.offer.ghosts.length === 0) { sim.offer.clearPick(); return CommandResult.InvalidPlacement; }
  }
  sim.events.add(SimEventKind.CardPicked, sim.offer.id, index);
  return CommandResult.Ok;
}

export function unpickCard(sim: LevelSim): CommandResult {
  if (!sim.offerOpen) return CommandResult.NoOffer;
  if (!sim.offer.hasPick) return CommandResult.NothingPicked;
  sim.offer.clearPick();
  sim.events.add(SimEventKind.CardUnpicked, sim.offer.id);
  return CommandResult.Ok;
}

export function placeSegment(sim: LevelSim, ghostIndex: number): CommandResult {
  if (!sim.offerOpen) return CommandResult.NoOffer;
  if (!sim.offer.hasPick) return CommandResult.NothingPicked;
  if (sim.offer.pickedCard.kind !== CardKind.Path) return CommandResult.WrongCardKind;
  if (ghostIndex < 0 || ghostIndex >= sim.offer.ghosts.length) return CommandResult.InvalidIndex;
  const placement = sim.offer.ghosts[ghostIndex];
  if (!sim.road.tryPlaceSegment(placement)) return CommandResult.InvalidPlacement;
  // The portal moved outward by k tiles: walkers keep their cells, so their distance from the portal grows by k.
  const k = Fix64.fromInt(placement.cells.length);
  for (const e of sim.enemySlots) if (e.alive) e.distance = e.distance.add(k);
  sim.events.add(SimEventKind.SegmentPlaced, placement.shapeIndex, placement.cells.length);
  sim.offersSinceRoad = paceAfterTaking(sim, CardKind.Path);
  chargePick(sim, sim.balance.cards.pickPointsCost.path);
  closeOffer(sim, false);
  return CommandResult.Ok;
}

/** Towers standing, the base excluded: the tower-offer interval is read at this count. */
export function towersBuilt(sim: LevelSim): number {
  let built = 0;
  for (const t of sim.towerSlots) if (t.alive && !sim.balance.towers[t.archetype].isBase) built++;
  return built;
}

export function placeTower(sim: LevelSim, cell: Cell): CommandResult {
  if (!sim.offerOpen) return CommandResult.NoOffer;
  if (!sim.offer.hasPick) return CommandResult.NothingPicked;
  if (sim.offer.pickedCard.kind !== CardKind.Tower) return CommandResult.WrongCardKind;
  if (!sim.road.tryPlaceTower(cell)) return CommandResult.InvalidPlacement;
  sim.troops.reroute();
  rerouteSquads(sim);
  const t = sim.allocateTower();
  t.id = sim.nextTowerId++;
  t.archetype = sim.offer.pickedCard.index;
  t.alive = true;
  t.cell = cell;
  t.tier = 1;
  t.cooldown = 0;
  t.disabledTicksLeft = 0;
  t.priority = TargetPriority.First;
  t.kills = 0;
  t.auraDamageMult = Fix64.One;
  t.auraPointsMult = Fix64.One;
  t.beamTarget = 0;
  t.beamTicks = 0;
  resetBranchState(t);
  sim.events.add(SimEventKind.TowerPlaced, t.id, cell.x, cell.y);
  recomputeAuras(sim);
  raiseSquad(sim, t);                                     // a Barracks musters its soldiers (TOWERS_V2)
  // A tower card in an opening road offer is one of the opening tower picks made now (DESIGN §4.4): that tower offer is
  // spent and this road offer comes back, so the opening still deals the same number of picks.
  if (sim.offer.kind === OfferKind.OpeningPath) { sim.openingTowerLeft--; sim.openingPathLeft++; }
  // In-run, a towers-due offer has already restarted the count (advancePace); a borrowed tower sets it back (below).
  // A rare's rally offer (M36) sits outside the pace: taking from it moves no count and charges nothing.
  if (sim.offer.kind === OfferKind.PostWave && !sim.offer.rare) sim.offersSinceTowers = paceAfterTaking(sim, CardKind.Tower);
  chargePick(sim, sim.balance.cards.pickPointsCost.tower);
  closeOffer(sim, false);
  return CommandResult.Ok;
}

/**
 * A pace's count once a card of its kind is taken from the open offer (DESIGN §4.4): 0 (the next one a full interval
 * from now), or, for a card borrowed ahead of its pace (handPlan.planHand), minus the offers it was early — it WAS the
 * next due card, so the one after it still comes a full interval after that, and the pace's rate is kept.
 */
function paceAfterTaking(sim: LevelSim, kind: CardKind): number {
  const plan = sim.offer.plan;
  return sim.offer.kind === OfferKind.PostWave && plan !== null && plan.borrowed === kind ? -plan.borrowedEarly : 0;
}

/**
 * A placed in-run card pushes the next offer back by its kind's cost (DESIGN §4.4): a tower now means the next offer
 * comes later, so taking one is a trade against the road and upgrades that offer would have brought.
 */
function chargePick(sim: LevelSim, cost: number): void {
  if (sim.offer.kind === OfferKind.PostWave && !sim.offer.rare && cost > 0) sim.nextOfferAt += cost;
}

/**
 * Declines the offer. An in-run offer pays `skipOfferPoints` toward the next one (DESIGN §4.8.7), so passing on cards
 * the player has no use for is a small step forward rather than nothing; the opening draft pays nothing.
 */
export function skipOffer(sim: LevelSim): CommandResult {
  if (!sim.offerOpen) return CommandResult.NoOffer;
  const reward = skipReward(sim);
  const id = sim.offer.id;
  closeOffer(sim, true);
  if (reward > 0) {
    sim.events.add(SimEventKind.SkipRewarded, id, reward);
    sim.addPoints(reward);
  }
  return CommandResult.Ok;
}

/** Points skipping the open offer pays: `skipOfferPoints` for an in-run offer, 0 for the opening draft, a rare's rally offer (M36) or no offer. */
export function skipReward(sim: LevelSim): number {
  return sim.offerOpen && sim.offer.kind === OfferKind.PostWave && !sim.offer.rare ? sim.balance.economy.skipOfferPoints : 0;
}

/**
 * Deals the open offer a fresh hand (DESIGN §4.4 "Rerolls", M46): the same number of cards, excluding the old ones where
 * the pool allows. An in-run hand is planned afresh (handPlan.rerollPlan): another mix of kinds whenever one is
 * possible, and tower cards open to it even when none were due. An opening road offer that may borrow a tower
 * (`earlyTowerAllowed`, never on level 1, whose walkthrough lays the road card by card) deals one tower card beside its
 * road. An opening tower offer and a rare's rally offer (M36) keep their own plan. A special card stays in the hand.
 */
export function reroll(sim: LevelSim): CommandResult {
  if (!sim.offerOpen) return CommandResult.NoOffer;
  if (sim.offer.hasPick) return CommandResult.AlreadyPicked;
  if (sim.rerollCharges <= 0) return CommandResult.NoRerollCharges;
  sim.rerollCharges--;
  const special = specialInHand(sim);
  const exclude = sim.offer.cards.filter((c) => c !== special);
  replan(sim, exclude);
  const drawn = sim.generator.fill(sim.offer, sim.road, sim.owned, sim.towerSlots, sim.rng, exclude);
  sim.events.add(SimEventKind.RerollUsed, sim.offer.id, sim.rerollCharges);
  if (drawn === 0) closeOffer(sim, false);
  else redealSpecial(sim, special);                       // the Ascend / skill card stays in the hand (sim/branches)
  return CommandResult.Ok;
}

/** The plan a reroll deals (see reroll); leaves the offer's plan as it is when nothing else fits. */
function replan(sim: LevelSim, old: readonly Card[]): void {
  const offer = sim.offer;
  const mix: HandMix = {
    towers: old.filter((c) => c.kind === CardKind.Tower).length,
    upgrade: old.some((c) => c.kind === CardKind.Upgrade),
    road: old.filter((c) => c.kind === CardKind.Path || c.kind === CardKind.Move).length,     // a Move card holds a road slot
  };
  const supply = handSupply(sim);
  // A tower card the board has no cell for is no card: towers are offered by a reroll only while a cell takes one.
  const placeable: HandSupply = supply.towerTypes > 0 && sim.road.collectValidTowerCells().length === 0 ? { ...supply, towerTypes: 0 } : supply;
  if (offer.kind === OfferKind.PostWave && !offer.rare && offer.plan !== null) {
    const due: DueKinds = { towers: offer.towersDue, upgrade: offer.upgradeDue, road: offer.roadDue };
    const plan = rerollPlan(sim.balance.cards, mix, due, paceCounters(sim), paceIntervals(sim), placeable, (n) => sim.rng.nextInt(n));
    if (plan !== null) offer.plan = plan;
  } else if (offer.kind === OfferKind.OpeningPath && offer.earlyTowerAllowed && sim.levelNumber > 1 && mix.towers === 0 && placeable.towerTypes > 0 && mix.road >= MIN_CHOICE) {
    // One of the opening tower picks, offered now (taking it brings this road offer back: placeTower).
    offer.plan = { towers: 1, upgrade: false, road: mix.road - 1, borrowed: null, borrowedEarly: 0 };
  }
}

/** Applies the picked Upgrade card: +1 tier on any tower, including the base. */
export function upgradeTower(sim: LevelSim, towerId: number): CommandResult {
  if (!sim.offerOpen) return CommandResult.NoOffer;
  if (!sim.offer.hasPick) return CommandResult.NothingPicked;
  if (sim.offer.pickedCard.kind !== CardKind.Upgrade) return CommandResult.WrongCardKind;
  const t = sim.findTower(towerId);
  if (t === null) return CommandResult.NoSuchTower;
  if (!sim.canUpgrade(t)) return CommandResult.MaxTier;
  if (t.tier === BASE_TIERS) ascend(sim, t);              // tier 3 → 4 on a type with a branch (sim/branches): TowerAscended + TowerUpgraded
  else {
    t.tier++;
    sim.events.add(SimEventKind.TowerUpgraded, t.id, t.tier);
  }
  // The fortress's tier makes its troops tougher too (DESIGN §7, troopTierMult).
  if (sim.balance.towers[t.archetype].isBase) sim.troops.setBaseTier(t.tier);
  promoteSquad(sim, t);                                   // a Barracks' soldiers too (TOWERS_V2)
  if (!sim.offer.rare) sim.offersSinceUpgrade = paceAfterTaking(sim, CardKind.Upgrade);
  recomputeAuras(sim);
  chargePick(sim, sim.balance.cards.pickPointsCost.upgrade);
  closeOffer(sim, false);
  return CommandResult.Ok;
}

/**
 * Spends the picked Move card (M55): the tower leaves its square for `cell`, a legal empty one, keeping its tier,
 * branch, kills, priority and cooldown. Its old square is free again; troops re-path round the new one, a Barracks'
 * squad takes the post nearest its new square, and auras are read afresh. Road pace is untouched: the road the slot
 * would have laid stays due.
 */
export function moveTower(sim: LevelSim, towerId: number, cell: Cell): CommandResult {
  if (!sim.offerOpen) return CommandResult.NoOffer;
  if (!sim.offer.hasPick) return CommandResult.NothingPicked;
  if (sim.offer.pickedCard.kind !== CardKind.Move) return CommandResult.WrongCardKind;
  const t = sim.findTower(towerId);
  if (t === null) return CommandResult.NoSuchTower;
  if (sim.balance.towers[t.archetype].isBase) return CommandResult.CannotSell;
  if (cell.equals(t.cell)) return CommandResult.InvalidPlacement;
  const from = t.cell;
  sim.road.removeTower(from);
  if (!sim.road.tryPlaceTower(cell)) {
    sim.road.tryPlaceTower(from);                         // put it back: the move never happened
    return CommandResult.InvalidPlacement;
  }
  t.cell = cell;
  t.beamTarget = 0;                                       // an Arcane Spire's beam lets go and locks on afresh
  t.beamTicks = 0;
  sim.troops.reroute();
  rerouteSquads(sim);
  relocateSquad(sim, t);
  sim.events.add(SimEventKind.TowerMoved, t.id, cell.x, cell.y);
  recomputeAuras(sim);
  chargePick(sim, sim.balance.cards.moveCard.pointsCost);
  closeOffer(sim, false);
  return CommandResult.Ok;
}

export function sellTower(sim: LevelSim, towerId: number): CommandResult {
  if (sim.phase === LevelPhase.WaveRunning) return CommandResult.RoadLocked;
  const t = sim.findTower(towerId);
  if (t === null) return CommandResult.NoSuchTower;
  if (sim.balance.towers[t.archetype].isBase) return CommandResult.CannotSell;
  sim.road.removeTower(t.cell);
  sim.troops.reroute();
  rerouteSquads(sim);
  disbandSquad(sim, t.id);
  t.alive = false;
  sim.events.add(SimEventKind.TowerSold, t.id, 0);
  recomputeAuras(sim);
  return CommandResult.Ok;
}

export function setPriority(sim: LevelSim, towerId: number, priority: number): CommandResult {
  const t = sim.findTower(towerId);
  if (t === null) return CommandResult.NoSuchTower;
  if (priority < 0 || priority > TargetPriority.Weakest) return CommandResult.InvalidIndex;
  t.priority = priority as TargetPriority;
  return CommandResult.Ok;
}

export function nextOpeningOffer(sim: LevelSim): void {
  if (sim.openingPathLeft > 0) { sim.openingPathLeft--; openOffer(sim, OfferKind.OpeningPath); }
  else if (sim.openingTowerLeft > 0) { sim.openingTowerLeft--; openOffer(sim, OfferKind.OpeningTower); }
  else sim.phase = LevelPhase.Ready;
}

export function openOffer(sim: LevelSim, kind: OfferKind): void {
  let plan: HandPlan | null = null, step: PaceStep | null = null;
  if (kind === OfferKind.PostWave) {
    // The intervals are read before the counters move, as the HUD's look-ahead reads them (cardPacing.lookAhead).
    const intervals = paceIntervals(sim);
    step = advancePace(sim, intervals);
    plan = planHand(sim.balance.cards, step, intervals, handSupply(sim));
    // Nothing due (road, towers and upgrades all between their paces), or a lone card with nothing to borrow beside it
    // (DESIGN §4.4): no offer shows and the points roll on toward the next threshold. This is how cards thin out late
    // in a level, once road cards come further apart.
    if (plan === null) return;
  }
  sim.offer.reset(++sim.offerCounter, kind);
  if (step !== null) {
    sim.offer.towersDue = step.towersDue;
    sim.offer.upgradeDue = step.upgradeDue;
    sim.offer.roadDue = step.roadDue;
    sim.offer.plan = plan;
  } else paceOpening(sim);
  const drawn = sim.generator.fill(sim.offer, sim.road, sim.owned, sim.towerSlots, sim.rng, null);
  if (drawn === 0) {
    sim.events.add(SimEventKind.OfferOpened, sim.offer.id, kind);
    closeOffer(sim, false);
    return;
  }
  sim.offerOpen = true;
  // Tier 4: an Ascend or skill card may ride the hand as an extra card, after its own (sim/branches/ascension.ts).
  if (kind === OfferKind.PostWave) dealSpecial(sim);
  sim.events.add(SimEventKind.OfferOpened, sim.offer.id, kind);
}

export function closeOffer(sim: LevelSim, skipped: boolean): void {
  sim.offerOpen = false;
  sim.offer.clearPick();
  sim.events.add(SimEventKind.OfferClosed, sim.offer.id, skipped ? 1 : 0);
  if (sim.phase === LevelPhase.OpeningDraft) nextOpeningOffer(sim);
}

/**
 * The in-run pacing counters (DESIGN §4.4): in-run offers since the last one due tower cards, and since the last
 * Upgrade and road piece were laid.
 */
export interface PaceCounters { readonly sinceTowers: number; readonly sinceUpgrade: number; readonly sinceRoad: number }
/** What one in-run offer is due, and the counters after it. */
export interface PaceStep { readonly towersDue: boolean; readonly upgradeDue: boolean; readonly roadDue: boolean; readonly next: PaceCounters }

/**
 * One in-run offer's pacing (DESIGN §4.4), pure so the HUD can look ahead with it: towers are due once every
 * `towerInterval` offers (read at the towers standing); an Upgrade `upgradeOfferInterval` offers after the last one
 * taken; road cards `roadInterval` offers after the last road piece laid (read at the road pace laid: every offer
 * early in the level, further apart as the road grows). A towers-due offer restarts its count whether or not a tower
 * is taken; an Upgrade or road stays due until one is taken (upgradeTower / placeSegment restart them), so a player
 * who takes the tower on an offer that also shows them sees them again next time — no pace starves another by
 * falling due on the same offers. A card borrowed ahead of its pace and taken sets its count below 0 (paceAfterTaking).
 */
export function paceStep(cards: CardsData, at: PaceCounters, towerInterval: number, roadInterval: number): PaceStep {
  const towersDue = at.sinceTowers + 1 >= towerInterval;
  const upgradeDue = at.sinceUpgrade + 1 >= cards.upgradeOfferInterval;
  const roadDue = at.sinceRoad + 1 >= roadInterval;
  return {
    towersDue, upgradeDue, roadDue,
    next: { sinceTowers: towersDue ? 0 : at.sinceTowers + 1, sinceUpgrade: at.sinceUpgrade + 1, sinceRoad: at.sinceRoad + 1 },
  };
}

/** The sim's pacing counters now. */
export function paceCounters(sim: LevelSim): PaceCounters {
  return { sinceTowers: sim.offersSinceTowers, sinceUpgrade: sim.offersSinceUpgrade, sinceRoad: sim.offersSinceRoad };
}

/** In-run offers between road-bearing ones at the road pace laid now (DESIGN §4.4). */
export function roadInterval(sim: LevelSim): number {
  return sim.balance.cards.roadOfferInterval(sim.road.paceLaid, sim.balance.tables.roadPaceTilesAt(sim.levelNumber));
}

/** The pace intervals as things stand: towers at the towers standing, road at the road pace laid. */
export function paceIntervals(sim: LevelSim): PaceIntervals {
  return { towers: sim.balance.cards.towerOfferInterval(towersBuilt(sim)), upgrade: sim.balance.cards.upgradeOfferInterval, road: roadInterval(sim) };
}

/** What the board can take now (handPlan.HandSupply). Read-only. */
export function handSupply(sim: LevelSim): HandSupply {
  return sim.generator.supply(sim.road, sim.owned, sim.towerSlots, (t) => sim.canUpgrade(t));
}

/** Decides what this in-run offer is due (DESIGN §4.4) and advances the counters. */
function advancePace(sim: LevelSim, intervals: PaceIntervals): PaceStep {
  const step = paceStep(sim.balance.cards, paceCounters(sim), intervals.towers, intervals.road);
  sim.offersSinceTowers = step.next.sinceTowers;
  sim.offersSinceUpgrade = step.next.sinceUpgrade;
  sim.offersSinceRoad = step.next.sinceRoad;
  return step;
}

/**
 * What an opening offer may be topped up with when only one shape fits (DESIGN §4.4): a road offer may borrow a tower
 * card from the opening tower offers still to come.
 */
function paceOpening(sim: LevelSim): void {
  sim.offer.earlyTowerAllowed = sim.offer.kind === OfferKind.OpeningPath && sim.openingTowerLeft > 0;
}

/** Opens a card offer (pausing the action) once points reach the next rising threshold. */
export function checkOfferThreshold(sim: LevelSim): void {
  if (sim.offerOpen || sim.points < sim.nextOfferAt) return;
  sim.offersOpened++;
  sim.nextOfferAt = sim.balance.cards.offerThreshold(sim.offersOpened + 1);
  openOffer(sim, OfferKind.PostWave);
}
